// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';

// Actions
import { fetchKMLBySourceBoundingBoxZoom, selectMapLayer } from '../actions';

// Shapes
import {
  mapLayersByProjCatTreeType, gMapsViewportPropertiesShape, mapLayerShape,
  projectCategoryFromCSSShape
} from '../propTypesShapes';
import { profileShape } from '../../authentication/propTypesShapes';

// Assets
import './Menu.css';

// Utils
import { isFirstRender } from '../../shared/utils/data';
import { ascendingAlphabeticalOrder } from '../../shared/utils/data';

// Constants
import { LEFT_PANE } from '../constants';


const createInitialTiers = (mapLayersByProjCatTree, selectedMapLayer, projectCategoryFromCSS) => {
  const tiers = {};
  let selectedProjectName = null;
  let selectedCategoryName = null;
  let selectedLayerName = null;

  if (projectCategoryFromCSS) {
    selectedProjectName = projectCategoryFromCSS.project;
    selectedCategoryName = projectCategoryFromCSS.category;
  } else if (selectedMapLayer) {
    selectedProjectName = selectedMapLayer.projectName;
    selectedCategoryName = selectedMapLayer.categoryName;
    selectedLayerName = selectedMapLayer.name;
  }

  Object.keys(mapLayersByProjCatTree).forEach(projectName => {
    const expandedProject = projectName === selectedProjectName;
    const numCategories = Object.keys(mapLayersByProjCatTree[projectName]).length;

    tiers[projectName] = {
      hasCategories: numCategories > 0,
      expanded: expandedProject,
      selectedCategory: expandedProject ? selectedCategoryName : null,
      categories: {}
    };

    Object.keys(mapLayersByProjCatTree[projectName]).forEach(categoryName => {
      const expandedCategory = expandedProject && categoryName === selectedCategoryName;
      /* If exist exactly 1 layer for each categoryName, then the layer will have the same name
        as the category.
      */
      const hasLayers = Object.keys(mapLayersByProjCatTree[projectName][categoryName]).length > 1;

      tiers[projectName].categories[categoryName] = {
        hasLayers,
        expanded: expandedCategory && categoryName !== selectedLayerName,
        selectedLayer: expandedCategory ? selectedLayerName : null
      };

      /* If exist exactly 1 category for each projectName and 1 layer for each categoryName, then
        the layer will have the same name as the project and the category.
      */
      if (numCategories === 1 && !hasLayers) {
        tiers[projectName].hasCategories = false;
      }
    });
  });

  return tiers;
};


class Menu extends React.PureComponent {
  /* Not use withRouter and pass the location & history properties due to
     https://github.com/ReactTraining/react-router/blob/v4.2.2/packages/
     react-router/docs/guides/blocked-updates.md */
  static propTypes = {
    token: PropTypes.string.isRequired,
    profile: profileShape,
    projectCategoryFromCSS: projectCategoryFromCSSShape,
    history: PropTypes.object,
    location: PropTypes.object,
    mapLayersByProjCatTree: mapLayersByProjCatTreeType,
    mapViewportProperties: gMapsViewportPropertiesShape,
    selectedMapLayer: mapLayerShape,
    isOnMobileView: PropTypes.bool
  };

  constructor(props) {
    super(props);

    const { mapLayersByProjCatTree, selectedMapLayer, projectCategoryFromCSS } = props;
    let tiers = null;

    if (!isFirstRender(mapLayersByProjCatTree)) {
      tiers = createInitialTiers(mapLayersByProjCatTree, selectedMapLayer, projectCategoryFromCSS);
    }

    this.state = { tiers };

    this.handleProjectOnClick = this.handleProjectOnClick.bind(this);
    this.handleCategoryOnClick = this.handleCategoryOnClick.bind(this);
    this.handleLayerOnClick = this.handleLayerOnClick.bind(this);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { mapLayersByProjCatTree, selectedMapLayer, projectCategoryFromCSS } = newProps;

    if (mapLayersByProjCatTree !== this.props.mapLayersByProjCatTree && mapLayersByProjCatTree) {
      this.setState({
        tiers: createInitialTiers(mapLayersByProjCatTree, selectedMapLayer, projectCategoryFromCSS)
      });
    }
  }

  selectProject(projectName) {
    const { tiers } = this.state;
    const newTiers = { ...tiers };

    Object.keys(newTiers).forEach(pName => {
      const project = tiers[pName];

      newTiers[pName] = {
        ...project,
        expanded: pName === projectName
          ? !project.expanded
          : false
      };
    });


    this.setState({ tiers: newTiers });
  }

  handleProjectOnClick(projectName) {
    const { mapLayersByProjCatTree } = this.props;
    const { tiers } = this.state;

    if ( !tiers[projectName].hasCategories ) {
      // _.find - Get the unique category
      this.showLayer( _.find(mapLayersByProjCatTree[projectName])[0] );
    }

    this.selectProject(projectName);
  }

  selectCategory(projectName, categoryName) {
    const { tiers } = this.state;
    const newTiers = { ...tiers };

    Object.keys(newTiers).forEach(pName => {
      const project = tiers[pName];

      newTiers[pName] = {
        ...project,
        selectedCategory: pName === projectName
          ? categoryName
          : false
      };

      Object.keys(newTiers[pName].categories).forEach(cName => {
        const category = project.categories[cName];

        newTiers[pName].categories[cName] = {
          ...category,
          expanded: cName === categoryName && category.hasLayers
            ? !category.expanded
            : false
        };
      });
    });

    this.setState({ tiers: newTiers });
  }

  showOrganizationsList() {
    const { history, location } = this.props;

    if (location.hash !== LEFT_PANE.HASH_LINK.LISTING) {
      history.push(`/${LEFT_PANE.HASH_LINK.LISTING}`);
    }
  }

  showMap() {
    const { history, location } = this.props;

    if (location.hash !== LEFT_PANE.HASH_LINK.MAP) {
      history.push(`/${LEFT_PANE.HASH_LINK.MAP}`);
    }
  }

  showLayer(layer) {
    const { token, profile, mapViewportProperties, isOnMobileView, dispatch } = this.props;

    if (layer && mapViewportProperties) {
      if (isOnMobileView) {
        this.showMap();
      } else {
        this.showOrganizationsList();
      }

      dispatch(selectMapLayer(token, profile, layer));
      dispatch(fetchKMLBySourceBoundingBoxZoom(token, layer, mapViewportProperties));
    }
  }

  handleCategoryOnClick(projectName, categoryName) {
    const { mapLayersByProjCatTree } = this.props;
    const { tiers } = this.state;

    if ( !tiers[projectName].categories[categoryName].hasLayers ) {
      this.showLayer( mapLayersByProjCatTree[projectName][categoryName][0] );
    }

    this.selectCategory(projectName, categoryName);
  }

  selectLayer(projectName, categoryName, layerName) {
    const { tiers } = this.state;
    const newTiers = { ...tiers };

    Object.keys(newTiers[projectName].categories).forEach(cName => {
      newTiers[projectName].categories[cName] = {
        ...tiers[projectName].categories[cName],
        selectedLayer: cName === categoryName
          ? layerName
          : false
      };
    });

    this.setState({ tiers: newTiers });
  }

  handleLayerOnClick(projectName, categoryName, layerName) {
    const { mapLayersByProjCatTree } = this.props;

    this.showLayer( mapLayersByProjCatTree[projectName][categoryName].find(layer => layer.name === layerName) );
    this.selectLayer(projectName, categoryName, layerName);
  }

  renderLayerMenu = (projectName, categoryName) => {
    const { mapLayersByProjCatTree } = this.props;
    const { tiers } = this.state;
    const layerOrderByName = (layerA, layerB) => {
      return ascendingAlphabeticalOrder(layerA.name, layerB.name);
    };

    return (
      <ul className={`MenuTierContainer collapse list-unstyled ${
        tiers[projectName].categories[categoryName].expanded ? 'show' :''}`}
      >
        {mapLayersByProjCatTree[projectName][categoryName].sort(layerOrderByName).map(
          (layer, index3) =>
            <li
              className={
                `MenuNavOption ${
                  layer.name === tiers[projectName].categories[categoryName].selectedLayer
                    ? 'active' : ''}`
              }
              key={index3}
            >
              <span className="MenuOptionLink"
                onClick={() => this.handleLayerOnClick(projectName, categoryName, layer.name)}
              >
                {layer.name}
              </span>
            </li>
        )}
      </ul>
    );
  };

  renderCategoryMenu = (projectName) => {
    const { mapLayersByProjCatTree } = this.props;
    const { tiers } = this.state;

    return (
      <ul className={`MenuTierContainer collapse list-unstyled ${
        tiers[projectName].expanded ? 'show' :''}`}
      >
        {Object.keys(mapLayersByProjCatTree[projectName]).sort(ascendingAlphabeticalOrder).map(
          (categoryName, index2) =>
            <li
              className={
                `MenuNavOption ${categoryName === tiers[projectName].selectedCategory
                  ? 'active' : ''}`
              }
              key={index2}
            >
              <span className="MenuOptionLink"
                data-toggle="collapse"
                aria-expanded={tiers[projectName].categories[categoryName].expanded}
                onClick={() => this.handleCategoryOnClick(projectName, categoryName)}
              >
                {/* In the case for rVIEW, if exist EXACTLY 1 layer for each categoryName
                then the layer will have the same name as the category */}
                { tiers[projectName].categories[categoryName].hasLayers
                  ? categoryName
                  : mapLayersByProjCatTree[projectName][categoryName][0].name
                }
              </span>

              {/* Layers */}

              {tiers[projectName].categories[categoryName].hasLayers &&
                this.renderLayerMenu(projectName, categoryName)
              }
            </li>
        )}
      </ul>
    );
  };

  renderProjectMenu = () => {
    const { mapLayersByProjCatTree } = this.props;
    const { tiers } = this.state;
    const projectsNames = Object.keys(mapLayersByProjCatTree).sort(ascendingAlphabeticalOrder);

    return (
      <ul className="MenuNavBar list-unstyled">
        {projectsNames.map((projectName, index) =>
          <li className="MenuNavOption" key={index}>
            <span
              className={`MenuOptionLink ${tiers[projectName].hasCategories ? 'icon-arrow' : ''}`}
              data-toggle="collapse"
              aria-expanded={tiers[projectName].expanded}
              onClick={() => this.handleProjectOnClick(projectName)}
            >
              {projectName.toUpperCase()}
            </span>

            {/* Categories */}

            {tiers[projectName].hasCategories &&
              this.renderCategoryMenu(projectName)
            }
          </li>
        )}
      </ul>
    );
  };

  render() {
    const { mapLayersByProjCatTree, projectCategoryFromCSS } = this.props;
    const { tiers } = this.state;

    if ( isFirstRender(mapLayersByProjCatTree) || isFirstRender(tiers) ) {
      return null;
    }

    return (
      <nav className="Menu">
        {projectCategoryFromCSS ? (
          this.renderLayerMenu(
            projectCategoryFromCSS.project,
            projectCategoryFromCSS.category
          )
        ) : (
          this.renderProjectMenu()
        )}
      </nav>
    );
  }
}

Menu.defaultProps = {
  isOnMobileView: false
};

export default connect(({ authentication, home }) => ({
  mapLayersByProjCatTree: home.mapLayersByProjCatTree,
  mapViewportProperties: home.mapViewportProperties,
  selectedMapLayer: home.selectedMapLayer,
  token: authentication.token,
  profile: authentication.profile,
  projectCategoryFromCSS: home.projectCategoryFromCSS
}), null)(Menu);
